import React, { useEffect, useMemo, useRef, useState } from "react";
import { SUITS, SYM, makePuzzle, pickMode } from "./bridgeSudoku";
import { colourSuits } from "../UI/SuitText";
import { getTreadmillDailyCount, incrementTreadmillDailyCount } from "../../utils/treadmillDailyTries";
import "./BridgeSudoku.css";

// Bridge Sudoku (Paul, 27 Oct 2026): "a flow of those, randomly generated ... some from defenders perspective
// some from declarer". Two hands seen as shapes, a few clues, fill in the other two hands. Like the article
// questions: no Check button - marked when every box is filled, one try again, then the answer.

const SUIT_CLS = { S: "bc-suit bc-suit--s", H: "bc-suit bc-suit--h", D: "bc-suit bc-suit--d", C: "bc-suit bc-suit--c" };
const MODES = [
  { id: "mix", label: "Mix" },
  { id: "defence", label: "Defending" },
  { id: "declarer", label: "Declaring" },
];
const SEAT_AREA = { N: "n", E: "e", S: "s", W: "w" };

// Game Plan daily: 3 solved (first or second go) = 5 points, once a day (Paul, 28 Oct 2026: "3 for 5 points").
const DAILY_GOAL = 3;

// Streak board (Paul, 28 Oct 2026): days in a row with the daily 3, one missed day a week allowed. Served by the
// content app; on localhost that is the content dev server on 3001.
const streakUrl = () => (typeof window !== "undefined" && /^(localhost|127.0.0.1)$/.test(window.location.hostname) ? "http://localhost:3001" : "") + "/api/sudoku-streaks";

function StreakBoard({ refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(streakUrl()).then((r) => (r.ok ? r.json() : null)).then((d) => { if (alive) setData(d || { top: [] }); }).catch(() => { if (alive) setData({ top: [] }); });
    return () => { alive = false; };
  }, [refreshKey]);
  return (
    <section className="bsu-streaks" aria-label="Streak leaderboard">
      <div className="bsu-streaks-h">
        <b>Streak leaderboard</b>
        <span>Days in a row getting 3 right. One missed day a week is allowed.</span>
      </div>
      {!data ? <p className="bsu-streaks-empty">Loading…</p> : data.top.length ? (
        <ol className="bsu-streaks-list">
          {data.top.map((r, i) => (
            <li key={i}>
              <span className="bsu-streaks-rank">{i + 1}</span>
              <span className="bsu-streaks-name">{r.name}</span>
              <span className="bsu-streaks-cur" title="Current streak">{r.current} {r.current === 1 ? "day" : "days"}</span>
              <span className="bsu-streaks-best" title="Best streak">best {r.best}</span>
            </li>
          ))}
        </ol>
      ) : <p className="bsu-streaks-empty">No streaks yet - get 3 right today to start one.</p>}
      {/* Same name as the Game Plan ladder; changed there (Paul, 28 Oct 2026). */}
      <p className="bsu-streaks-name-note">Names are the same as on the Game Plan ladder. <a href="/game-plan#ladder">Change yours on your Game Plan</a></p>
    </section>
  );
}

// A free account gets ONE puzzle a day; members unlimited; guests are asked to make an account (Paul, 28 Oct 2026).
const FREE_PER_DAY = 1;
const TRAINER_KEY = "bridge-sudoku";

export default function BridgeSudokuTrainer({ onFiveRight, access = "member", uid = "", onSubscribe }) {
  const [freeUsed, setFreeUsed] = useState(() => (access === "free" ? getTreadmillDailyCount(uid || "preview", TRAINER_KEY) : 0));
  const [mode, setMode] = useState("mix");
  const [puzzle, setPuzzle] = useState(() => makePuzzle(pickMode("mix")));
  // Each box is marked the moment a number goes in (Paul, 28 Oct 2026: "as user enters it instantly tick or say try
  // again per number"). Right = locked with a tick. Wrong = red, emptied, "try again"; a second miss on the same box
  // shows its answer. The puzzle is finished when every box is right or shown.
  const [cells, setCells] = useState({}); // key -> { st: 'right' | 'retry' | 'shown', bad }
  const [score, setScore] = useState({ right: 0, played: 0, solved: 0 });
  const [boardKey, setBoardKey] = useState(0);
  // The daily points, said plainly in the card once the 3rd is right (Paul, 28 Oct 2026: "make sure it clearly says +5
  // points after user does the daily sudoku"). 'fresh' = earned now, 'already' = earned earlier today.
  const [pointsMsg, setPointsMsg] = useState(null);
  const toldRef = useRef(false);
  const root = useRef(null);

  const boxes = useMemo(() => (puzzle ? puzzle.unknown.flatMap((seat) => SUITS.map((s) => `${seat}${s}`)) : []), [puzzle]);
  const answerOf = (key) => puzzle.hands[key[0]][SUITS.indexOf(key[1])];
  const done = boxes.length > 0 && boxes.every((k) => cells[k] && (cells[k].st === "right" || cells[k].st === "shown"));
  const rightAll = done && boxes.every((k) => cells[k].st === "right");
  const clean = rightAll && boxes.every((k) => !cells[k].missed);

  function next(m = mode) {
    setPuzzle(makePuzzle(pickMode(m)));
    setCells({});
    setPointsMsg(null);
  }

  // Score once, when the last box is settled.
  const scoredRef = useRef(null);
  useEffect(() => {
    if (!done || scoredRef.current === puzzle) return;
    scoredRef.current = puzzle;
    if (access === "free") setFreeUsed(incrementTreadmillDailyCount(uid || "preview", TRAINER_KEY));
    setScore((sc) => {
      const solved = sc.solved + (rightAll ? 1 : 0);
      if (solved >= DAILY_GOAL && !toldRef.current && (onFiveRight || access === "member")) {
        toldRef.current = true;
        setPointsMsg("fresh");
        const p = onFiveRight ? onFiveRight() : null;
        if (p && typeof p.then === "function") p.then((res) => { if (res && res.fresh === false) setPointsMsg("already"); });
        setTimeout(() => setBoardKey((k) => k + 1), 2500); // the new day shows on the board once the stamp is saved
      }
      return { right: sc.right + (clean ? 1 : 0), played: sc.played + 1, solved };
    });
  }, [done]);

  function focusNext(el) {
    if (!root.current) return;
    setTimeout(() => {
      const ins = Array.from(root.current.querySelectorAll("input.bsu-in"));
      const i = ins.indexOf(el);
      const nx = (i >= 0 ? ins.slice(i + 1).concat(ins.slice(0, i)) : ins).find((x) => x.value === "");
      if (nx) nx.focus({ preventScroll: true });
    }, 0);
  }

  function type(key, e) {
    const v = e.target.value.replace(/[^0-9]/g, "").slice(-1);
    if (v === "") return;
    const el = e.target;
    const cur = cells[key] || {};
    if (Number(v) === answerOf(key)) {
      setCells((c) => ({ ...c, [key]: { st: "right", missed: !!cur.missed } }));
      focusNext(el);
    } else if (cur.st === "retry") {
      setCells((c) => ({ ...c, [key]: { st: "shown", missed: true, bad: v } }));
      focusNext(el);
    } else {
      setCells((c) => ({ ...c, [key]: { st: "retry", missed: true, bad: v } }));
      el.value = "";
    }
  }

  // Local dev only: the screenshot script (scripts/_shoot-bridge-sudoku.js) reads the puzzle on show from here.
  if (typeof window !== "undefined" && /^(localhost|127.0.0.1)$/.test(window.location.hostname)) window.__bsuPuzzle = puzzle;
  if (!puzzle) return <p>Could not make a puzzle - press Next.</p>;
  const outOfFree = access === "free" && freeUsed >= FREE_PER_DAY && !done;
  // Guests see the real puzzle with a small sign-up prompt (Paul, 28 Oct 2026: "rather just put the same screen ... with a
  // small nice prompt to create account ... make it visually appealing to want to play").
  const guest = access === "guest";
  const toSignup = () => {
    const el = document.querySelector(".tm-signupPanel");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  if (outOfFree) {
    return (
      <div className="bsu">
        <div className="bsu-lock">
          <div className="bsu-lock-icon" aria-hidden="true">&#129513;</div>
          {(
            <>
              <b>That's today's free puzzle.</b>
              <span>Members play as many as they like, plus +5 daily points and the streak leaderboard.</span>
              {onSubscribe ? <button type="button" className="bsu-next" onClick={onSubscribe}>Become a member</button> : null}
              <span className="bsu-lock-small">Or come back tomorrow for another free one.</span>
            </>
          )}
        </div>
        <StreakBoard refreshKey={boardKey} />
      </div>
    );
  }

  const seat = (s) => {
    const seen = puzzle.seen.includes(s);
    return (
      <div key={s} className={`bsu-hand bsu-${SEAT_AREA[s]}${seen ? "" : " is-unseen"}${puzzle.who[s] === "You" ? " is-you" : ""}`}>
        {/* A coloured name bar on every hand, YOU in orange so it stands out wherever you sit (Paul, 28 Oct 2026). */}
        <div className={`bsu-l bsu-l--${String(puzzle.who[s]).toLowerCase()}`}>{puzzle.who[s] === "You" ? "★ You" : puzzle.who[s]}</div>
        {SUITS.map((su, i) => {
          const key = `${s}${su}`;
          const n = puzzle.hands[s][i];
          let cell;
          const cs = cells[key] || {};
          if (seen) cell = <span className="bsu-v">{n}</span>;
          else if (cs.st === "right") cell = <span className="bsu-v">{n}<b className="bsu-tick" aria-label="right">✓</b></span>;
          else if (cs.st === "shown") cell = <span className="bsu-v">{n}<em className="bsu-you">you said {cs.bad}</em></span>;
          else cell = (
            <input className="bsu-in" type="text" inputMode="numeric" autoComplete="off" placeholder="?"
              aria-label={`${puzzle.who[s]} ${SYM[su]}`} data-key={key} defaultValue="" readOnly={guest}
              onFocus={guest ? (e) => { e.target.blur(); toSignup(); } : undefined} onChange={(e) => type(key, e)} />
          );
          const st = seen ? "" : cs.st === "right" ? " is-right" : cs.st === "shown" ? " is-wrong" : cs.st === "retry" ? " is-retry" : "";
          return (
            <div key={su} className={`bsu-row${su === puzzle.trump ? " is-trump" : ""}${st}`}>
              <span className={SUIT_CLS[su]}>{SYM[su]}</span>{cell}{cs.st === "retry" ? <em className="bsu-again">✗ {cs.bad}, try again</em> : null}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bsu" ref={root}>
      <div className="bsu-top">
        <div className="bsu-modes" role="tablist" aria-label="Which side">
          {MODES.map((m) => (
            <button key={m.id} type="button" role="tab" aria-selected={mode === m.id}
              className={`bsu-mode${mode === m.id ? " is-on" : ""}`} onClick={() => { setMode(m.id); next(m.id); }}>{m.label}</button>
          ))}
        </div>
        <div className="bsu-score">
          <span className="bsu-goal">{Math.min(score.solved, DAILY_GOAL)} / {DAILY_GOAL} right today{score.solved >= DAILY_GOAL ? " ✓" : ""}</span>
          <span>{score.right} / {score.played} right first time</span>
        </div>
      </div>

      <div className="bsu-card">
        {pointsMsg ? (
          <div className={`bsu-points${pointsMsg === "fresh" ? "" : " is-already"}`} role="status" aria-live="polite">
            {pointsMsg === "fresh"
              ? <><b>+5 points!</b> <span>Today's Bridge Sudoku points are on your Game Plan.</span></>
              : <><b>Well done!</b> <span>You already have today's 5 points - come back tomorrow for more.</span></>}
          </div>
        ) : null}
<div className="bsu-card-in">
        <div className="bsu-tag">
          <span className={`bsu-side bsu-side--${puzzle.mode}`}>{puzzle.mode === "defence" ? "Defending" : "Declaring"}</span>
          <span className="bsu-contract">Contract <b>{puzzle.level}<span className={SUIT_CLS[puzzle.trump]}>{SYM[puzzle.trump]}</span></b>{puzzle.mode === "declarer" ? " by you" : " by declarer"}</span>
        </div>
        <ol className="bsu-clues">
          {puzzle.clues.map((c, i) => <li key={i}>{colourSuits(c)}</li>)}
        </ol>
        <div className="bsu-table" key={puzzle.clues.join("|") + puzzle.contract}>
          {["N", "W", "E", "S"].map(seat)}
          {/* A little table in the middle, so the seats read the right way round (Paul, 28 Oct 2026). */}
          <div className="bsu-mid">
            <div className="bsu-felt" aria-label={`Contract ${puzzle.contract}`}>
              {["N", "W", "E", "S"].map((x) => (
                <span key={x} className={`bsu-felt-${x.toLowerCase()}${puzzle.seen.find((x) => puzzle.who[x] === "You") === x ? " is-you" : ""}`}>{x}</span>
              ))}
              <span className="bsu-felt-c">{puzzle.level}<span className={SUIT_CLS[puzzle.trump]}>{SYM[puzzle.trump]}</span></span>
            </div>
          </div>
        </div>
        <div className="bsu-bar">
          {done ? (
            <>
              <span className={`bsu-result${rightAll ? " is-right" : ""}`}>{clean ? "Congrats!" : rightAll ? "Got it" : "Bad luck - this is the answer."}</span>
              {access === "free" && freeUsed >= FREE_PER_DAY
                ? (onSubscribe ? <button type="button" className="bsu-next" onClick={onSubscribe}>Members: play more</button> : null)
                : <button type="button" className="bsu-next" onClick={() => next()}>Next</button>}
            </>
          ) : guest ? (
            <div className="bsu-join">
              <span><b>Create a free account to play</b> - 1 puzzle a day free, unlimited for members.</span>
              <button type="button" className="bsu-next" onClick={toSignup}>Create free account</button>
            </div>
          ) : <span className="bsu-hint">Fill in the two hidden hands</span>}
        </div>
      </div></div>
      <StreakBoard refreshKey={boardKey} />
    </div>
  );
}
